import { createContext, useContext, useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { getUserProfiles, type UserProfile as Profile } from '@/db/userProfiles';

interface ProfilesContextValue {
  profiles: Profile[];
  profilesMap: Map<string, Profile>;
  /** Resolve user_id → display name. Falls back to input if not found. */
  resolveDisplayName: (idOrName: string) => string;
  /** Get full profile by id */
  getProfile: (id: string) => Profile | undefined;
  loaded: boolean;
}

const ProfilesContext = createContext<ProfilesContextValue>({
  profiles: [],
  profilesMap: new Map(),
  resolveDisplayName: (x) => x,
  getProfile: () => undefined,
  loaded: false,
});

export function ProfilesProvider({ children }: { children: ReactNode }) {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getUserProfiles()
      .then(setProfiles)
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const profilesMap = useMemo(() => {
    const map = new Map<string, Profile>();
    for (const p of profiles) {
      map.set(p.id, p);
    }
    return map;
  }, [profiles]);

  const resolveDisplayName = useCallback(
    (idOrName: string): string => {
      const profile = profilesMap.get(idOrName);
      return profile ? profile.display_name : idOrName;
    },
    [profilesMap]
  );

  const getProfile = useCallback(
    (id: string) => profilesMap.get(id),
    [profilesMap]
  );

  return (
    <ProfilesContext.Provider value={{ profiles, profilesMap, resolveDisplayName, getProfile, loaded }}>
      {children}
    </ProfilesContext.Provider>
  );
}

export function useProfilesContext() {
  return useContext(ProfilesContext);
}
